
import {
  computed, defineComponent, onMounted, PropType, Ref, ref, watch,
} from 'vue';
import { useRoute } from 'vue-router';
/* eslint-disable import/no-cycle */
import { useMainStore } from '@/store/mainStore';
import AppLoader from '@/components/UI/AppLoader.vue';
/* eslint-disable import/no-cycle */
import SpacesService from '@/modules/spaces/services/SpacesService';
import { IGroupData, IGroupInvitation, IPhoto } from '@/types/interfaces';
import SpacesUser from '@/modules/spaces/components/SpacesUser.vue';
import AppHeader from '@/components/AppHeader.vue';
import AddGroupImage from '@/modules/spaces/components/AddGroupImage.vue';
import GroupEditPopup from '@/modules/spaces/components/GroupEditPopup.vue';
import { NETWORK_CONTACT_TYPES, PHOTO_VIEW_TYPE } from '@/types/enums';
import SpacesManageMembersPopUp from '@/modules/spaces/components/SpacesManageMembersPopUp.vue';
import AppMessageInput from '@/components/AppMessageInput.vue';
import GroupSettingsPopup from '@/modules/spaces/components/GroupSettingsPopup.vue';
import { lowResUrlByViewTypeByUrl } from '@/helpers/lowResPhotoUrl';

export default defineComponent({
  emits: ['showPopUp', 'setGroupData'],
  components: {
    GroupSettingsPopup,
    SpacesManageMembersPopUp,
    AppLoader,
    SpacesUser,
    AppHeader,
    AddGroupImage,
    GroupEditPopup,
    AppMessageInput,
  },
  props: {
    userType: { type: Number as PropType<NETWORK_CONTACT_TYPES>, required: true },
  },
  setup(props) {
    const mainStore = useMainStore();
    const route = useRoute();
    const dataRequest = ref(true);

    const groupInfo = ref({} as IGroupData);
    const membersText = computed(() => {
      const length = groupInfo?.value?.members?.length || 0;
      if (length === 0 || length > 1) {
        return 'Members';
      }
      return 'Member';
    });

    const activeName = ref('overview');
    const tabName = ref('overview');
    const groupEdit = ref(false);
    const memberEdit = ref(false);
    const settingsEdit = ref(false);

    const type: Ref<NETWORK_CONTACT_TYPES> = ref(props.userType);
    const groupUsers = ref([] as string[]);

    const usersToInvite = ref([] as string[]);
    const usersToDelete = ref([] as string[]);

    const posts = ref([]);
    const membersOverview = computed(() => groupInfo.value?.members?.slice(0, 5) || []);
    /* eslint-disable global-require */
    const memberIcon = computed(() => require('@/assets/img/icons-img/user.svg'));
    /* eslint-enable global-require */

    const groupCover = computed(() => {
      const coverUrl = groupInfo.value.photo?.url;
      return coverUrl ? lowResUrlByViewTypeByUrl(coverUrl, PHOTO_VIEW_TYPE.WHSML) : '';
    });

    function fetchGroupData() {
      dataRequest.value = true;
      SpacesService.getGroupById(route.params.id as string).then((response) => {
        if (response.success) {
          groupInfo.value = response.results.data;
          dataRequest.value = false;
        }
        // TODO add error handling (404)
      });
    }

    function handleUpdateCoverPhoto(cover: IPhoto) {
      groupInfo.value.photo = cover;
    }

    function handleOnGroupInfoDone(data: IGroupData) {
      groupInfo.value = data;
      groupEdit.value = false;
    }

    function updateUsersToAdd(accountId: string) {
      if (groupUsers.value.includes(accountId)) {
        const x = groupUsers.value.splice(groupUsers.value.indexOf(accountId), 1);
        usersToDelete.value.push(x[0]);
      } else if (usersToDelete.value.includes(accountId) && membersOverview.value.some((el: any) => el.accountId === accountId)) {
        const x = usersToDelete.value.splice(usersToDelete.value.indexOf(accountId), 1);
        groupUsers.value.push(x[0]);
      } else if (usersToInvite.value.includes(accountId)) {
        usersToInvite.value.splice(usersToInvite.value.indexOf(accountId), 1);
      } else {
        usersToInvite.value.push(accountId);
      }
    }

    function clearContactsManage() {
      groupUsers.value = []; usersToDelete.value = []; usersToInvite.value = [];
    }

    function pushContacts() {
      const payload: IGroupInvitation[] = usersToInvite.value.map((el) => (
        { targetId: el, groupId: groupInfo.value.id, initiatorId: mainStore.user.accountId }
      ));
      SpacesService.inviteUsersToGroup(payload).then((response) => {
        if (usersToDelete.value.length) {
          return SpacesService.deleteUsersFromGroup(groupInfo.value.id, usersToDelete.value);
        }
        return response;
      }).then((response) => {
        clearContactsManage();
        return fetchGroupData();
      }).then((response) => {
        memberEdit.value = false;
      })
        .finally(() => { memberEdit.value = false; });
    }

    function handleManage() {
      if (activeName.value === 'overview') {
        groupEdit.value = true;
      }
      if (activeName.value === 'members') {
        groupUsers.value = groupInfo.value.members.map((el: { accountId: string }) => el.accountId);
        memberEdit.value = true;
      }
    }

    function handleSettings() {
      settingsEdit.value = true;
    }

    function getMemberPhoto(photo: IPhoto | undefined) {
      return photo ? lowResUrlByViewTypeByUrl(photo.url, PHOTO_VIEW_TYPE.WHSML) : memberIcon.value;
    }

    watch(() => activeName.value, (nv, ov) => {
      if (nv !== ov) {
        groupEdit.value = false;
      }
    });

    onMounted(() => {
      fetchGroupData();
    });

    return {
      activeName,
      dataRequest,
      groupInfo,
      membersText,
      membersOverview,
      memberIcon,
      mainStore,
      posts,
      groupCover,
      groupEdit,
      memberEdit,
      tabName,
      groupUsers,
      usersToInvite,
      usersToDelete,
      handleSettings,
      settingsEdit,
      clearContactsManage,
      fetchGroupData,

      getMemberPhoto,
      handleManage,
      handleUpdateCoverPhoto,
      handleOnGroupInfoDone,
      updateUsersToAdd,
      pushContacts,
      NETWORK_CONTACT_TYPES,
    };
  },
});
